import { Spacer } from "@/components/Spacer";
import { useEffect } from "react";
import { useState } from "react";
import { motion, useInView, rgba } from "framer-motion";
import { useRef } from "react";
import Lottie from "lottie-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import Section0301 from "@/assets/lottie/section-03-01.json";

export default function Section03({ isMobile }: { isMobile: boolean }) {
  const getNewLineWhenMobile = () => {
    return isMobile ? "\n" : "";
  };

  const sectionRef = useRef(null);
  const isSectionInView = useInView(sectionRef, {
    once: true,
    amount: 0.5,
  });

  return (
    <div className="flex flex-col items-center desktop:py-[100px] desktop:px-[32px] py-[80px] px-[24px] bg-[#ffffff]">
      <motion.div
        id="section-03-01"
        className="flex flex-col items-center"
        ref={sectionRef}
        initial={{ opacity: 0, y: 30 }}
        animate={isSectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 80,
          mass: 1,
          delay: 0.2,
        }}
      >
        <div
          className="font-pre-bold text-[#5364F2] desktop:text-[20px] text-[14px] leading-[1.2em]"
          style={{ letterSpacing: -0.02 }}
        >
          증상이 있으신가요?
        </div>

        <Spacer height={12} />
        <div className="font-pre-bold text-[#17191C] desktop:text-[32px] text-[24px] leading-[150%] whitespace-pre-line text-center">
          원하는 방식으로{getNewLineWhenMobile()} 예상 질환을 확인해보세요
        </div>
        <Spacer height={isMobile ? 16 : 4} />
        <div className="font-pre-regular text-[#17191C] desktop:text-[24px] text-[15px] leading-[150%]">
          간단하게 선택하거나, 자유롭게 챗봇과 대화할 수 있어요
        </div>
      </motion.div>
      <Spacer height={isMobile ? 40 : 60} />
      <AiChat isMobile={isMobile} />
      <Spacer height={24} />
      <Survey isMobile={isMobile} />
    </div>
  );
}

const AiChat = ({ isMobile }: { isMobile: boolean }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "0px 0px -20% 0px",
  });

  return (
    <motion.div
      ref={ref}
      className="desktop:max-w-[960px] max-w-[342px] w-full bg-[#EFEEFC] desktop:pl-[80px] desktop:h-[400px] desktop:pt-[0px] pt-[32px] flex desktop:flex-row flex-col rounded-[20px]"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.2,
      }}
    >
      <div className="w-full h-full flex flex-col justify-center items-start desktop:pl-[0px] pl-[32px]">
        <Tip
          title="AI 챗봇"
          backgroundColor="rgba(83, 100, 242, 0.15)"
          textColor="#5364F2"
        />
        <Spacer height={isMobile ? 12 : 16} />
        <div className="whitespace-pre-line font-pre-bold desktop:text-[24px] text-[20px] leading-[140%] text-[#17191C]">
          이상 증상부터 건강 궁금증까지{"\n"}
          헬시어 AI에게 물어보세요
        </div>
        <Spacer height={isMobile ? 12 : 24} />
        <div
          className="whitespace-pre-line font-pre-medium desktop:text-[18px] text-[15px] leading-[150%] text-[#2E323A]"
          style={{ letterSpacing: -0.02 }}
        >
          CIERA 모델을 탑재한 헬시어 닥터 AI와 함께{"\n"}
          언제 어디서나 전문적인 건강 상담을 받아보세요.
        </div>
      </div>

      <div className="w-full h-full flex desktop:items-end items-center justify-center pt-[34px]">
        <div
          id="lottie-container"
          className="desktop:h-max desktop:w-[565px] px-[0px] w-full h-full flex desktop:items-end items-center justify-center"
        >
          <DotLottieReact
            src={"/assets/lottie/section-03-01.lottie"}
            autoplay
            loop
          />
        </div>
      </div>
    </motion.div>
  );
};

const Survey = ({ isMobile }: { isMobile: boolean }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "0px 0px -20% 0px",
  });

  return (
    <motion.div
      ref={ref}
      className="desktop:max-w-[960px] max-w-[342px] w-full bg-[#EBF7FC] desktop:pr-[80px] desktop:h-[400px] desktop:pt-[0px] pt-[32px] flex desktop:flex-row flex-col-reverse rounded-[20px]"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.2,
      }}
    >
      <div className="desktop:w-max w-[342px] h-full flex items-end justify-center desktop:pt-[0px] pt-[34px]">
        <div
          id="lottie-container"
          className="desktop:w-[565px] desktop:h-[290px] w-full h-full flex items-end justify-center"
        >
          <DotLottieReact
            src={"/assets/lottie/section-03-02.lottie"}
            autoplay
            loop
          />
        </div>
      </div>

      <div className="h-full flex flex-col justify-center items-start desktop:pl-[50px] pl-[32px]">
        <Tip
          title="선택형 문진"
          backgroundColor="rgba(0,180,255,0.15)"
          textColor="#00B4FF"
        />
        <Spacer height={isMobile ? 12 : 16} />
        <div className="whitespace-pre-line font-pre-bold desktop:text-[24px] text-[20px] leading-[140%] text-[#17191C]">
          빠른 체크, 간단한{"\n"}
          선택으로 확인해요
        </div>
        <Spacer height={isMobile ? 12 : 24} />
        <div
          className="whitespace-pre-line font-pre-medium desktop:text-[18px] text-[15px] leading-[150%] text-[#2E323A]"
          style={{ letterSpacing: -0.02 }}
        >
          맞춤형 질문을 통해 빠르게{"\n"}
          예상질환을 확인할 수 있어요
        </div>
      </div>
    </motion.div>
  );
};

const Tip = ({
  title,
  backgroundColor,
  textColor,
}: {
  title: string;
  backgroundColor: string;
  textColor: string;
}) => {
  return (
    <div
      className="desktop:px-[12px] px-[14px] esktop:py-[12px] py-[8px] rounded-[8px] flex justify-center items-center w-max"
      style={{ backgroundColor: backgroundColor }}
    >
      <span
        className="font-pre-bold desktop:text-[18px] text-[15px] leading-[1.2em]"
        style={{ letterSpacing: -0.02, color: textColor }}
      >
        {title}
      </span>
    </div>
  );
};
